"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Code as Code2, FileText, Link2, ChartBar as BarChart3, Zap, Target, CircleCheck as CheckCircle2, ArrowRight, ShieldCheck } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const auditItems = [{
  id: "technical",
  title: "Technical Analysis",
  badge: "Foundation",
  icon: Code2,
  description: <>
        I check how search engines crawl and index your site to ensure no hidden technical roadblocks are blocking your visibility. Deeper remediation is also available through hands-on{" "}
        <Link to="/TechnicalSeoPage" className="text-primary underline underline-offset-2 hover:text-primary/80 font-medium">
          technical SEO support
        </Link>
        .
      </>,
  deliverables: ["Crawlability & indexation check", "Site structure & canonical cleanups", "XML sitemap & robots.txt review"]
}, {
  id: "onpage",
  title: "On-Page Review",
  badge: "Relevance",
  icon: FileText,
  description: "An evaluation of your key page content, titles, metadata, and internal link structure to align precisely with search intent.",
  deliverables: ["Target keyword & intent alignment", "Title tag & meta description audit", "Internal linking optimization"]
}, {
  id: "backlinks",
  title: "Backlink Profile",
  badge: "Authority",
  icon: Link2,
  description: "A complete health check of your link portfolio to identify high-value incoming links, potentially problematic backlinks, and anchor text distribution.",
  deliverables: ["Link quality & backlink quality assessment", "Domain authority & trust signals", "Unlinked brand mention opportunities"]
}, {
  id: "competitors",
  title: "Competitor Benchmarking",
  badge: "Market Edge",
  icon: BarChart3,
  description: "I analyze your top search rivals to uncover their strategies, content gaps, and ranking tactics you can leverage.",
  deliverables: ["Keyword gap & overlap analysis", "Competitor backlink strategies", "Market share & visibility comparison"]
}, {
  id: "ux-vitals",
  title: "User Experience & Core Web Vitals",
  badge: "Performance",
  icon: Zap,
  description: "I assess real-world speed metrics, mobile usability, and design friction points that impact rankings and conversion rates.",
  deliverables: ["Core Web Vitals speed check", "Mobile navigation & usability assessment", "Conversion friction identification"]
}, {
  id: "opportunities",
  title: "Opportunity Identification",
  badge: "Action Plan",
  icon: Target,
  description: "Clear, prioritized quick-wins and strategic recommendations designed to identify commercially relevant search opportunities.",
  deliverables: ["Commercially relevant search opportunities", "Prioritized quick-win checklist", "30-60-90 day strategic roadmap"]
}];
export default function AuditComponents() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="auditcomponents" data-nav="light" className="bg-background text-foreground py-20 md:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 mb-4">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-medium uppercase tracking-wider">
              No Jargon. Just Clear Insights.
            </Badge>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground font-serif">
            What is Included in My SEO Audit
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Every audit is conducted directly by me, Yassine Essaadi. I break down your website&apos;s performance into six practical, clear components with actionable steps to grow your organic reach.
          </p>
        </div>

        {/* Audit Components Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {auditItems.map((item, index) => {
          const IconComponent = item.icon;
          return <motion.div key={item.id} variants={itemVariants} data-index={index}>
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-all duration-300 flex flex-col justify-between group">
                  <CardHeader className="space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="p-3 rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary group-hover:text-primary-foreground">
                        <IconComponent className="size-6" />
                      </div>
                      <Badge variant="outline" className="text-xs font-normal border-border">
                        {item.badge}
                      </Badge>
                    </div>
                    <div>
                      <CardTitle className="text-xl font-bold text-card-foreground font-serif">
                        {item.title}
                      </CardTitle>
                      <CardDescription className="mt-2 text-sm text-muted-foreground leading-relaxed">
                        {item.description}
                      </CardDescription>
                    </div>
                  </CardHeader>

                  <CardContent className="pt-0 flex-1 flex flex-col justify-end">
                    <Separator className="my-4" />
                    <div className="space-y-2.5">
                      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground block mb-2">
                        Key Deliverables:
                      </span>
                      {item.deliverables.map((deliverable, dIndex) => <div key={dIndex} className="flex items-start gap-2.5 text-xs sm:text-sm text-card-foreground">
                          <CheckCircle2 className="size-4 text-accent shrink-0 mt-0.5" />
                          <span>{deliverable}</span>
                        </div>)}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Personal Assurance & CTA Strip */}
        <div className="mt-16 bg-muted/60 rounded-2xl p-6 sm:p-8 md:p-10 border border-border flex flex-col md:flex-row items-center justify-between gap-8">
          <div className="flex items-start gap-4">
            <div className="p-3 rounded-full bg-primary text-primary-foreground shrink-0 hidden sm:flex">
              <ShieldCheck className="size-6" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold text-foreground font-serif">
                Direct Consultant Delivery
              </h3>
              <p className="text-sm text-muted-foreground mt-1 max-w-2xl leading-relaxed">
                You receive a clear, plain-English breakdown with every recommendation prioritized by business impact, so you know exactly what to tackle first without getting lost in technical clutter.
              </p>
            </div>
          </div>

          <div className="shrink-0 w-full md:w-auto flex flex-col sm:flex-row gap-3">
            <Button size="lg" className="w-full sm:w-auto gap-2" asChild>
              <Link to="/ContactPage">
                <span>Book a Free SEO Consultation</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
            <Button size="lg" variant="outline" className="w-full sm:w-auto" asChild>
              <Link to="/CaseStudiesPage">
                <span>Explore My Case Studies</span>
              </Link>
            </Button>
          </div>
        </div>

      </div>
    </section>;
}